/* eslint-disable react/prop-types */
/**
 * @Description Container for Fasat Loader
 * @FileName fasatLoader.js
 * @Author SAMAPTI TALUKDAR-talusam
 * @CreatedOn 04 March, 2021 02:25:06
 * @IssueID 623
 */
import React, { useEffect, useRef, useState } from 'react';
import { Progress, colors } from '@manulife/mux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { staticCommonLabelKeys } from '../../moduleConstants';
import numericConstants from '../../util/numericConstants';

const Overlay = styled.div`
  top: 0;
  left: 0;
  position: fixed;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  z-index: 100001;
  cursor: wait;
  pointer-events: all;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
`;

function FasatLoader({ id = `loader-${Math.random().toString(numericConstants.THIRTY_SIX).substring(numericConstants.TWO, numericConstants.NINE)}` }) {
  const [t] = useTranslation();
  const loadingText = t(staticCommonLabelKeys.ARIA_LOADING_TXT);

  // Reference to the live region element
  const liveRegionRef = useRef(null);
  // Track mounted state to ensure we don't have race conditions
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    // Set mounted state to true
    setIsMounted(true);
    return () => {
      // Set mounted state to false when unmounting
      setIsMounted(false);
    };
  }, []);

  // Function to trigger the announcement
  const announceLoading = () => {
    if (liveRegionRef.current && document.body.contains(liveRegionRef.current)) {
      // Generate a unique message with timestamp to force screen readers to announce again
      const timestamp = new Date().getTime();
      liveRegionRef.current.setAttribute('data-timestamp', timestamp);

      // First clear the content
      liveRegionRef.current.textContent = '';

      // Then set the content after a short delay
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = loadingText;
        }
      }, numericConstants.FIFTY);
    }
  };

  const initialTimeout = useRef(null);
  const intervalId = useRef(null);
  useEffect(() => {
    if (isMounted) {
    // Immediate announcement on mount with longer delay to ensure DOM is ready
      initialTimeout.current = setTimeout(() => {
        announceLoading();
      }, numericConstants.FIFTY);
      announceLoading();

      // Create an interval that updates the live region every 10 seconds
      intervalId.current = setInterval(announceLoading, numericConstants.TEN_THOUSAND);
    }
    // Clean up interval and timeout on unmount
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      if (initialTimeout.current) {
        clearTimeout(initialTimeout.current);
      }
    };
  }, [isMounted]);

  return (
    <Overlay>
      <LoaderContainer>
        <Progress size={80} color={colors.m_green} />
      </LoaderContainer>
      {/* Visually hidden live region for screen readers */}
      <div
        id={id}
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        className="sr-only"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: '0',
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: '0',
        }}
      >
        Loading
      </div>
    </Overlay>
  );
}

export default FasatLoader;
